import { Injectable } from "@angular/core";
import { ReducerUtilityService } from "src/app/shared/utility-services/reducer-utility.service";
import { SAVE_QUOTE_DOCUMENT_UPLOAD_HANDOFF_STATUS, SaveQuoteMetaData } from "../interfaces/save-quote-data.interface";
import { SavedQuotesStateStructure } from "../interfaces/savedQuotesStateStructure.interface";
import { SAVED_QUOTES_REDUCER_NAME, saveSavedQuotes } from "../reducers/saved-quotes.reducer";
import { StateStructure } from "@ptl/state";
import { NgRedux } from "@ptl/angular-redux-store";
import { CommonUtilityService } from "src/app/shared/utility-services/common-utility.service";
import { saveIndividualState } from "src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/reducers/individual-market.reducer";
import { hotswapPlansState } from "src/app/angular-elements/plan-selection/configs/reducers/plans.reducer";
import { hotswapPlanDetailsState } from "src/app/angular-elements/plan-selection/configs/reducers/plan-details.reducer";
import { saveApplicationSubmissionInfo } from "src/app/shared/reducers/enrollmentApp.reducer";
import { saveReferralType } from "src/app/angular-elements/shared/components/configs/reducers/globalData.reducer";
import { storeLifeEventDocumentUploadState } from "src/app/angular-elements/shared/components/configs/reducers/life-event.reducer";
import { saveSpecialEnrollmentCircumstanceState } from "src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/reducers/special-enrollment-circumstance.reducer";
import { saveAgencyAndAgentValues } from "src/app/angular-elements/shared/components/individual-market-consumer-referral-email/services/link-share.reducer";
import { PtlSessionIndividualObject } from "src/app/angular-elements/shared/components/ptl-session-loader/interfaces/ptl-session.interface";
import { RetrieveApp } from "src/app/shared/interfaces/application-retrieval/retrieveApp.interface";
import { URL_PARAM_REFERRAL_TYPE } from "src/app/angular-elements/shared/components/configs/interfaces/globalStateStructure.interface";
import { ApplicationRecord } from "../../interfaces/application-record.interface";
import { UserInfoService } from "src/app/angular-elements/shared/services/user-info.service";
import { IndividualMarketStateStructure } from "src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/individualMarket.interfaceStateStructure";
import { PlansStateStructure } from "src/app/angular-elements/plan-selection/configs/interfaces/plansStateStructure.interface";
import { PlanDetailsStateStructure } from "src/app/angular-elements/plan-selection/configs/interfaces/planDetailsStateStructure.interface";
import { ZipCountyStateStructure } from "src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface";
import { EnrollmentAppStateStructure } from "src/app/angular-elements/shared/interfaces/enrollmentAppStateStructure.interface";
import { LifeEventStateStructure } from "src/app/angular-elements/shared/components/configs/interfaces/life-event.state-structure.interface";
import { SpecialEnrollmentCircumstanceStateStructure } from "src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/special-enrollment-circumstance.StateStructure.interface";
import { ENROLLMENT_CHANNEL, EnrollPayloadSaveRequest } from "src/app/angular-elements/shared/interfaces/enrollment-submission.interface";
import { firstValueFrom } from "rxjs";
import { SAVE_QUOTE_CARD_TYPE } from "../interfaces/saved-quotes.input.interface";
import { IndividualMarketsEnrollmentSubmissionService } from "src/app/angular-elements/shared/services/enrollment/individual-markets/enrollment-submission.individual-markets.service";
import { EntitlementsHelperService } from "@ptl/account-management";
import { ApplicationStatusRetrievalService } from "src/app/shared/services/application-status-retrieval.service";
import { HttpErrorResponse } from "@angular/common/http";
import { EffectiveDateStateStructure } from "src/app/angular-elements/shared/components/configs/interfaces/effective-date.state-structure.interface";
import { setEffectiveDate, storeEffectiveDateState } from "src/app/angular-elements/shared/components/configs/reducers/effective-date.reducer";

interface ProducerInfo {
  savedData: RetrieveApp,
  plan: ApplicationRecord
}
interface ConsumerInfo {
  savedData: PtlSessionIndividualObject,
  referralId: string
}
interface LoadQuoteDataParams  {
  producerInfo?: ProducerInfo,
  consumerInfo?: ConsumerInfo
}


@Injectable({
  providedIn: "root",
})
export class IndividualMarketSaveQuoteUtilityService {

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private commonUtilityService: CommonUtilityService,
    private userInfoService: UserInfoService,
    private individualMarketsEnrollmentSubmissionService: IndividualMarketsEnrollmentSubmissionService,
    private entitlementsHelperService: EntitlementsHelperService,
    private appStatusRetrieveService: ApplicationStatusRetrievalService
  ) {}

  getSavedQuoteConfigsFromResumedApp(): SaveQuoteMetaData {
    const state: SavedQuotesStateStructure = this.getSavedQuoteStateData();
    if (state.saveQuoteData) {
      return state.saveQuoteData;
    }
    return null;
  }

  getSavedQuoteStateData(): SavedQuotesStateStructure {
    const state: SavedQuotesStateStructure = this.reducerUtilityService.getComponentStates()[SAVED_QUOTES_REDUCER_NAME];
    return state;
  }

  async loadSaveQuoteDataToState(params: LoadQuoteDataParams): Promise<void> {

    const isLoggedIn = await firstValueFrom(this.userInfoService.isLoggedIn$);

    /**
     * 1) SETUP DATA ANCHORS
     */
    let states: {
      individualMarketState: IndividualMarketStateStructure;
      planState: PlansStateStructure;
      planDetailsState: PlanDetailsStateStructure;
      zipCountyState: ZipCountyStateStructure;
      enrollmentApp: EnrollmentAppStateStructure;
      lifeEvent: LifeEventStateStructure;
      "special-enrollment-circumstance": SpecialEnrollmentCircumstanceStateStructure,
      effectiveDate: EffectiveDateStateStructure,
      saveQuotesState: SavedQuotesStateStructure
    } = {
      individualMarketState: null,
      planState: null,
      planDetailsState: null,
      zipCountyState: null,
      enrollmentApp: null,
      lifeEvent: null,
      "special-enrollment-circumstance": null,
      effectiveDate: null,
      saveQuotesState: null
    }

    /**
     * 2) POPULATE DATA ANCHORS
     */
    if ( isLoggedIn ) {
      const pData = params.producerInfo;
      const pSavedData = pData.savedData;
      const enrollmentObj: EnrollPayloadSaveRequest = pSavedData.enrollementObject as EnrollPayloadSaveRequest;
      const saveQuoteData = enrollmentObj.saveQuoteData;
      const referralId = (enrollmentObj.referralid && enrollmentObj.referralid.toString()) || null;
      
      states.individualMarketState = saveQuoteData.state.payloadStates.individualMarket;
      states.planState = saveQuoteData.state.componentStates.Plans;
      states.planDetailsState = saveQuoteData.state.componentStates.PlanDetails;
      states.zipCountyState = saveQuoteData.state.componentStates.ZipCounty;
      states.enrollmentApp = saveQuoteData.state.componentStates.enrollmentApp || {} as unknown as EnrollmentAppStateStructure; // PRODUCER SPECIFIC
      states.enrollmentApp.appId = params.producerInfo.plan.applicationTrackingId; // PRODUCER SPECIFIC
      states.lifeEvent = saveQuoteData.state.componentStates.LifeEvent;
      states.effectiveDate = saveQuoteData.state.appStates.effectiveDate;
      states["special-enrollment-circumstance"] = saveQuoteData.state.payloadStates['special-enrollment-circumstance'];
      states.saveQuotesState = {
        enrollmentChannel: ENROLLMENT_CHANNEL.AGENT_GENERIC,
        enrollmentFlow: enrollmentObj.saveQuoteData.enrollmentFlow,
        referralId: referralId,
        producerInd: true,
        saveQuoteName: pData.plan.statusDescription,
        saveQuoteData: saveQuoteData
      }
    } else {

      const cData = params.consumerInfo;
      const cSavedData = cData.savedData;

      states.individualMarketState = cSavedData.individualMarketState;
      states.planState = cSavedData.planState;
      states.planDetailsState = cSavedData.planDetailsState;
      states.zipCountyState = cSavedData.zipCountyState;
      states.enrollmentApp = cSavedData.enrollmentState;
      states.lifeEvent = cSavedData.lifeEventDocumentUploadState;
      states.effectiveDate = cSavedData.effectiveDateState;
      states["special-enrollment-circumstance"] = cSavedData['special-enrollment-circumstance']
      states.saveQuotesState = {
        enrollmentChannel: ENROLLMENT_CHANNEL.AGENT_GENERIC, // purposefully "Producer"
        enrollmentFlow: cSavedData.saveQuoteData.enrollmentFlow,
        referralId: cData.referralId,
        producerInd: true,
        saveQuoteName: cSavedData.saveQuoteName,
        saveQuoteData: cSavedData.saveQuoteData
      }

      const agentName = `${cSavedData.agentName.firstName} ${cSavedData.agentName.lastName}`;
      this.ngRedux.dispatch(saveAgencyAndAgentValues(cSavedData.agencyId, cSavedData.agentId, agentName));
    }
    
    /**
     * 3) USE DATA ANCHORS
     */
    this.commonUtilityService.saveCountyZipData(states.zipCountyState.county, states.zipCountyState.zipCode);

    this.ngRedux.dispatch(hotswapPlanDetailsState(states.planDetailsState));
    this.ngRedux.dispatch(hotswapPlansState(states.planState));
    this.ngRedux.dispatch(saveIndividualState(states.individualMarketState));
    this.ngRedux.dispatch(saveSavedQuotes(states.saveQuotesState));
    
    this.ngRedux.dispatch(saveReferralType(URL_PARAM_REFERRAL_TYPE.SAVED_QUOTE));
        
    /** Effective Date */
    this.ngRedux.dispatch(storeEffectiveDateState(states.effectiveDate));

    /** Application Tracking Id ( for resaving app ) */
    this.ngRedux.dispatch(saveApplicationSubmissionInfo(states.enrollmentApp));

    /** Document Upload ( SEP Life Events ) */
    this.ngRedux.dispatch(storeLifeEventDocumentUploadState(states.lifeEvent));
    
    /** Special Enrollment Older Pattern */
    const hasOldSEPData = (states.individualMarketState?.['special-enrollment-child-date'] && states.individualMarketState?.['special-enrollment-child-date'] != "");
    if (hasOldSEPData) {
      const payloadStates = this.reducerUtilityService.getPayloadStates();
      payloadStates['special-enrollment-interstitial'] = { isApplicableCircumstance: "Yes" };
      payloadStates['special-enrollment-child'] = { hasAcquiredChild: "Yes" };
      payloadStates['special-enrollment-child-date'] = { childAcquisitionDate: states.individualMarketState?.['special-enrollment-child-date'] };
    }
    
    /** Special Enrollment Circumstance Data */
    const specialEnrollmentCircumstanceState = states['special-enrollment-circumstance'];
    if (specialEnrollmentCircumstanceState) {
      this.ngRedux.dispatch(saveSpecialEnrollmentCircumstanceState(specialEnrollmentCircumstanceState));
    }
  }

  /**
   * Prior to hand-off, add some additional information stemming from dashboard interactions
   */
  updateSaveQuoteMetaData(responseObj: RetrieveApp, quoteType: SAVE_QUOTE_CARD_TYPE, applicationRecord: ApplicationRecord): SaveQuoteMetaData {

    const saveQuoteEnrollmentObj: EnrollPayloadSaveRequest = responseObj.enrollementObject as EnrollPayloadSaveRequest;
    const saveQuoteMetaData = saveQuoteEnrollmentObj.saveQuoteData;

    // Update ActionList.documentUpload
    if (quoteType === SAVE_QUOTE_CARD_TYPE.DOCUMENT_UPLOAD_REQUEST) {
      
      // (1) - ActionList check
      /**(1a) - Update Payload */
      if (!saveQuoteMetaData.actionList) {
        saveQuoteMetaData.actionList = {};
      }
      /** (1b) - Update Dashboard Card */
      if (!applicationRecord.enrollmentObjectJson.saveQuoteData.actionList) {
        applicationRecord.enrollmentObjectJson.saveQuoteData.actionList = {};
      }

      // (2) - ActionList.DocumentUpload check
      /** (2a) - Update Payload */
      if (!saveQuoteMetaData.actionList.documentUpload) {
        saveQuoteMetaData.actionList.documentUpload = {
          status: null
        };
      }
      /** (2b) - Update Dashboard Card */
      if (!applicationRecord.enrollmentObjectJson.saveQuoteData.actionList.documentUpload) {
        applicationRecord.enrollmentObjectJson.saveQuoteData.actionList.documentUpload = {
          status: null
        }
      }

      // (3) - ActionList.DocumentUpload.Status update
      /** Subsequent interactions, update document upload status */
      saveQuoteMetaData.actionList.documentUpload.status = SAVE_QUOTE_DOCUMENT_UPLOAD_HANDOFF_STATUS.STEP_1_REQUESTED;
      applicationRecord.enrollmentObjectJson.saveQuoteData.actionList.documentUpload.status = SAVE_QUOTE_DOCUMENT_UPLOAD_HANDOFF_STATUS.STEP_1_REQUESTED;

      // (4) - ApplicationTrackingId assignment
      /** 
       * Because applicationTrackingId isn't generate UNTIL AFTER FIRST SAVE
       * There will be a scenario where the enrollementObject doesn't have it yet [ would get it on re-saves or submissions ]
       * 
       * Add it from the dashboard retrieval if needed
       */
      if (!saveQuoteEnrollmentObj.applicationTrackingId) {
        saveQuoteEnrollmentObj.applicationTrackingId = responseObj.applicationTrackingId;
      }

      // RE-SAVE APP to pass info about the document upload steps
      this.individualMarketsEnrollmentSubmissionService.resaveApp(saveQuoteEnrollmentObj);
    }

    return saveQuoteMetaData;
  }

  async retrieveApp(applicationRecord: ApplicationRecord): Promise<RetrieveApp | HttpErrorResponse> {
    const selectedAgency = await this.entitlementsHelperService.getSelectedAgencyPromise();

    let appId = applicationRecord.applicationTrackingId;
    let code = applicationRecord.agencyCode || selectedAgency.agencyId;

    let response: RetrieveApp | HttpErrorResponse = await this.appStatusRetrieveService.getApplication(appId, code);
      if(response instanceof HttpErrorResponse){
        console.error(
          `No Application Found!
          AppId: ${appId}
          AgencyCode (Optional): ${code}`
        );
      }
    return response;
  }
}
